import * as React from "react";

import { addToLabel } from '@api/v4/bulk_actions/assets';
import { promiseReflect, ReflectInterface, PromiseStatus } from '@api/v4/utils';

import { pluralizeObject } from '@helpers/show_page_helpers';

import LabelTree from '@components/show_page/bulk_actions/sub_components/LabelTree';
import { LabelTree as LabelTreeType } from '@api/v4/resources/types/labelsTypes';

import renderModal from '../modals/renderModal';

import './styles/BulkAddLabelModal.scss';

// ensure all requests complete and collect any errors or failures
const addToLabelHandler = async ({
  selectedLabels,
  selectedAssetKeys,
  closeModal
}): Promise<void> => {
  const multipleLabels = selectedLabels.length > 1;
  const multipleAssets = selectedAssetKeys.length > 1;
  try {
    const promiseArray = selectedLabels.map((label: string) => promiseReflect(addToLabel(Array.from(selectedAssetKeys), label)));
    // will not throw if any promises fails, we will filter below
    const results = await Promise.all(promiseArray);

    if (results.some((result: ReflectInterface) => result.status === PromiseStatus.Rejected)) throw new Error('Add to Label Failure');
    Notify.create({ title: t(`${multipleAssets ? 'Assets' : 'Asset'} added successfully to ${multipleLabels ? 'labels' : 'label'}.`), type: "success" });
  } catch (error) {
    Notify.create({ title: t("Something went wrong. Try again soon!"), type: "error" });
  } finally {
    closeModal();
  }
};

interface BulkLabelModalProps {
  labelTree: LabelTreeType;
  selectedAssetKeys: string[];
  closeModal: () => void;
}

const BulkAddLabelModal: React.FunctionComponent<BulkLabelModalProps> = ({
  selectedAssetKeys,
  closeModal,
  labelTree
}): JSX.Element => {
  const [selectedLabels, setSelectedLabels] = React.useState<string[]>([]);
  const assetQtyObject = pluralizeObject(selectedAssetKeys);

  React.useEffect(() => closeModal, [closeModal]);

  const modalButtonRow = (): JSX.Element => (
    <div className="button-container">
      <button
        className="button primary sm t-confirm-modal"
        type="button"
        disabled={selectedLabels.length === 0}
        onClick={(): Promise<void> => addToLabelHandler({ selectedLabels, selectedAssetKeys, closeModal })}
      >
        {t('Save')}
      </button>
    </div>
  );

  return (
    <div className="modal-content-wrapper bulk-label-modal">
      <div className="modal-content-wrapper__header">
        <span className="bff-label icon" aria-hidden="true" />
        <h3 className="modal-title">{t('Add to your Labels')}</h3>
        <button
          className="close-button"
          onClick={closeModal}
          type="button"
        >
          <span className="bff-close" />
        </button>
      </div>
      <div className="modal-content-wrapper__body">
        <h3 className="body-title">{t('Add')}
          <span className="bold-text">
            {assetQtyObject.qty} {t('asset')}{assetQtyObject.plural}
          </span>
          {t('to one or more Labels')}
        </h3>
        <div className="modal-row">
          <LabelTree
            labelTree={labelTree}
            selectedLabels={selectedLabels}
            setSelectedLabels={setSelectedLabels}
          />
        </div>
        <div>
          {modalButtonRow()}
        </div>
      </div>
    </div>
  );
};

const ModalComponent = renderModal(BulkAddLabelModal, 'BulkAddLabelModal');
export default ModalComponent;
